import React, { useState } from "react";
import { Plus, Search, Edit, Trash2, FileText, CheckCircle2, User, Loader2, Printer, Download, Building, FileCheck } from "lucide-react";
import { usePartners } from "../hooks/usePartners";
import { useAgreements } from "../hooks/useAgreements";
import { useCompanyLogos } from "../hooks/useCompanyLogos";
import PartnerModal from "../components/modals/PartnerModal";
import PartnerAgreementModal from "../components/modals/PartnerAgreementModal";
import { exportAgreementToWord, printAgreement } from "../utils/agreementExport";

export default function Partners() {
  const {
    partners,
    categories,
    loading: loadingPartners,
    error: errorPartners,
    addPartner,
    updatePartner,
    deletePartner,
    uploadPartnerAttachment,
    deletePartnerAttachment
  } = usePartners();

  const {
    templates,
    agreements,
    loading: loadingAgreements,
    error: errorAgreements,
    addAgreement,
    updateAgreement,
    deleteAgreement
  } = useAgreements();

  const { activeLogoUrl, allCompanyLogos } = useCompanyLogos();

  // Navigation tab: "partners" or "agreements"
  const [mainTab, setMainTab] = useState("partners");

  // Partners Directory Search & Filter
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");

  // Agreements Search & Filter
  const [agreementSearch, setAgreementSearch] = useState("");
  const [agreementStatusFilter, setAgreementStatusFilter] = useState("All");

  // Partner Modal states
  const [partnerModalOpen, setPartnerModalOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);

  // Agreement Modal states
  const [agreementModalOpen, setAgreementModalOpen] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState(null);

  // Handlers for Partners
  const handleOpenAddPartner = () => {
    setSelectedPartner(null);
    setPartnerModalOpen(true);
  };

  const handleOpenEditPartner = (partner) => {
    setSelectedPartner(partner);
    setPartnerModalOpen(true);
  };

  const handleDeletePartner = async (partner) => {
    if (!window.confirm(`Are you sure you want to delete partner "${partner.name}"? All associated attachment documents will remain in storage but the reference will be removed.`)) {
      return;
    }
    try {
      await deletePartner(partner.id);
    } catch (err) {
      console.error("Error deleting partner:", err);
    }
  };

  const handleSavePartner = async (data) => {
    try {
      if (selectedPartner) {
        await updatePartner(selectedPartner.id, data);
      } else {
        await addPartner(data);
      }
      setPartnerModalOpen(false);
    } catch (err) {
      console.error("Error saving partner:", err);
    }
  };

  // Handlers for Agreements
  const handleOpenAddAgreement = () => {
    setSelectedAgreement(null);
    setAgreementModalOpen(true);
  };

  const handleOpenEditAgreement = (agreement) => {
    setSelectedAgreement(agreement);
    setAgreementModalOpen(true);
  };

  const handleDeleteAgreement = async (agreement) => {
    if (!window.confirm(`Are you sure you want to delete agreement "${agreement.title}"?`)) {
      return;
    }
    try {
      await deleteAgreement(agreement.id);
    } catch (err) {
      console.error("Error deleting agreement:", err);
    }
  };

  const handleSaveAgreement = async (data) => {
    try {
      if (selectedAgreement) {
        await updateAgreement(selectedAgreement.id, data);
      } else {
        await addAgreement(data);
      }
      setAgreementModalOpen(false);
    } catch (err) {
      console.error("Error saving agreement:", err);
    }
  };

  const handleExportWord = (agr) => {
    const tpl = templates.find((t) => t.id === agr.templateId) || templates[0];
    exportAgreementToWord(agr, tpl, agr.primeCompanyLogo || activeLogoUrl);
  };

  const handlePrint = (agr) => {
    const tpl = templates.find((t) => t.id === agr.templateId) || templates[0];
    printAgreement(agr, tpl, agr.primeCompanyLogo || activeLogoUrl);
  };

  // Filtering Partners
  const filteredPartners = partners.filter((partner) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesName = partner.name.toLowerCase().includes(searchLower);
    const matchesCategoryName = (partner.categoryName || "").toLowerCase().includes(searchLower);
    const matchesPocs = partner.pocs && partner.pocs.some((poc) =>
      (poc.name && poc.name.toLowerCase().includes(searchLower)) ||
      (poc.title && poc.title.toLowerCase().includes(searchLower)) ||
      (poc.email && poc.email.toLowerCase().includes(searchLower)) ||
      (poc.phone && poc.phone.toLowerCase().includes(searchLower))
    );
    const matchesSearch = matchesName || matchesCategoryName || matchesPocs;
    const matchesFilter = categoryFilter === "All" || partner.categoryName === categoryFilter;
    return matchesSearch && matchesFilter;
  });

  // Filtering Agreements
  const filteredAgreements = agreements.filter((agr) => {
    const searchLower = agreementSearch.toLowerCase();
    const matchesTitle = (agr.title || "").toLowerCase().includes(searchLower);
    const matchesPartner = (agr.partnerName || agr.partnerCompany || "").toLowerCase().includes(searchLower);
    const matchesCustomer = (agr.customerName || agr.customerAcronym || "").toLowerCase().includes(searchLower);
    const matchesSearch = matchesTitle || matchesPartner || matchesCustomer;
    const matchesStatus = agreementStatusFilter === "All" || agr.status === agreementStatusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="animate-fade-in" style={{ padding: "1.5rem" }}>
      {/* Page Header */}
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
        <div>
          <h1 className="page-title" style={{ margin: 0, fontSize: "1.75rem", fontWeight: 700, color: "var(--text-primary)" }}>
            Partners & Agreements
          </h1>
          <p className="text-secondary" style={{ margin: "0.25rem 0 0 0", fontSize: "0.875rem" }}>
            Manage active company partnerships, Teaming Agreements, NDAs, and contract documents.
          </p>
        </div>
        {mainTab === "partners" ? (
          <button className="btn btn-primary" onClick={handleOpenAddPartner}>
            <Plus size={18} style={{ marginRight: "0.5rem" }} /> Add Partner
          </button>
        ) : (
          <button className="btn btn-primary" onClick={handleOpenAddAgreement}>
            <Plus size={18} style={{ marginRight: "0.5rem" }} /> Create Agreement
          </button>
        )}
      </div>

      {/* Main Tab Navigation */}
      <div style={{ display: "flex", gap: "0.5rem", borderBottom: "1px solid var(--border-color)", marginBottom: "1.5rem" }}>
        <button
          type="button"
          onClick={() => setMainTab("partners")}
          className={`btn ${mainTab === "partners" ? "btn-primary" : "btn-secondary"}`}
          style={{ padding: "0.45rem 1.25rem", fontSize: "0.9rem", borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
        >
          <Building size={16} style={{ marginRight: "0.4rem" }} /> Partners Directory ({partners.length})
        </button>
        <button
          type="button"
          onClick={() => setMainTab("agreements")}
          className={`btn ${mainTab === "agreements" ? "btn-primary" : "btn-secondary"}`}
          style={{ padding: "0.45rem 1.25rem", fontSize: "0.9rem", borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
        >
          <FileCheck size={16} style={{ marginRight: "0.4rem" }} /> Agreements ({agreements.length})
        </button>
      </div>

      {/* TAB 1: PARTNERS DIRECTORY */}
      {mainTab === "partners" && (
        <div className="animate-fade-in">
          {/* Toolbar */}
          <div className="card glass-card" style={{ padding: "1rem", marginBottom: "1.5rem", display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
            <div className="input-with-icon" style={{ flex: 1, minWidth: "250px" }}>
              <Search className="input-icon" size={18} />
              <input
                type="text"
                className="form-input icon-padding"
                placeholder="Search by company name, category, or POC..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ margin: 0 }}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span className="text-secondary" style={{ fontSize: "0.875rem", fontWeight: 500 }}>Category:</span>
              <select
                className="form-input"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{ margin: 0, padding: "0.4rem 2rem 0.4rem 1rem", fontSize: "0.875rem", height: "38px", backgroundColor: "var(--bg-card)" }}
              >
                <option value="All">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* List Container */}
          {loadingPartners ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "4rem" }}>
              <Loader2 className="spinner-icon" size={32} />
              <span style={{ marginLeft: "0.75rem", color: "var(--text-secondary)" }}>Loading partners...</span>
            </div>
          ) : errorPartners ? (
            <div className="alert alert-danger">{errorPartners}</div>
          ) : filteredPartners.length === 0 ? (
            <div className="card glass-card" style={{ padding: "3rem", textAlign: "center", color: "var(--text-secondary)" }}>
              <p style={{ margin: 0, fontSize: "0.95rem", fontStyle: "italic" }}>No partners found matching the criteria.</p>
            </div>
          ) : (
            <div className="card glass-card" style={{ padding: 0, overflowX: "auto" }}>
              <table className="table" style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--border-color)", backgroundColor: "rgba(255,255,255,0.01)" }}>
                    <th style={{ padding: "1rem 1.5rem", fontWeight: 600, fontSize: "0.85rem", color: "var(--text-muted)" }}>Company</th>
                    <th style={{ padding: "1rem 1.5rem", fontWeight: 600, fontSize: "0.85rem", color: "var(--text-muted)" }}>Category</th>
                    <th style={{ padding: "1rem 1.5rem", fontWeight: 600, fontSize: "0.85rem", color: "var(--text-muted)" }}>Points of Contact</th>
                    <th style={{ padding: "1rem 1.5rem", fontWeight: 600, fontSize: "0.85rem", color: "var(--text-muted)" }}>Active Agreements</th>
                    <th style={{ padding: "1rem 1.5rem", fontWeight: 600, fontSize: "0.85rem", color: "var(--text-muted)", width: "100px", textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPartners.map((partner) => {
                    const ndasCount = partner.attachments?.filter((a) => a.type === "NDA").length || 0;
                    const teamingCount = partner.attachments?.filter((a) => a.type === "Teaming Agreement").length || 0;
                    const otherCount = partner.attachments?.filter((a) => a.type === "Other").length || 0;
                    const partnerPlatformAgreements = agreements.filter((a) => a.partnerId === partner.id);

                    return (
                      <tr
                        key={partner.id}
                        style={{ borderBottom: "1px solid var(--border-color)", transition: "background-color 0.2s" }}
                        className="hover-row"
                      >
                        {/* Company info */}
                        <td style={{ padding: "1rem 1.5rem", verticalAlign: "top" }}>
                          <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>{partner.name}</div>
                          <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginTop: "0.25rem" }}>
                            Created {new Date(partner.createdAt).toLocaleDateString()}
                          </div>
                        </td>

                        {/* Category */}
                        <td style={{ padding: "1rem 1.5rem", verticalAlign: "top" }}>
                          <span className="badge badge-secondary" style={{ fontSize: "0.78rem" }}>
                            {partner.categoryName}
                          </span>
                        </td>

                        {/* POC List */}
                        <td style={{ padding: "1rem 1.5rem", verticalAlign: "top" }}>
                          {partner.pocs && partner.pocs.length > 0 ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                              {partner.pocs.map((poc, index) => (
                                <div key={index} style={{ fontSize: "0.85rem" }}>
                                  <div style={{ fontWeight: 500, color: "var(--text-primary)" }}>
                                    {poc.name} {poc.title && <span style={{ fontWeight: 400, color: "var(--text-secondary)" }}>({poc.title})</span>}
                                  </div>
                                  <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                                    {poc.email} {poc.phone && `• ${poc.phone}`}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span style={{ fontSize: "0.85rem", fontStyle: "italic", color: "var(--text-muted)" }}>No contacts.</span>
                          )}
                        </td>

                        {/* Attachments & Platform Agreements statistics */}
                        <td style={{ padding: "1rem 1.5rem", verticalAlign: "top" }}>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                            {partnerPlatformAgreements.length > 0 && (
                              <span
                                className="badge"
                                onClick={() => {
                                  setMainTab("agreements");
                                  setAgreementSearch(partner.name);
                                }}
                                style={{
                                  fontSize: "0.72rem",
                                  backgroundColor: "rgba(59, 130, 246, 0.2)",
                                  color: "#60A5FA",
                                  border: "1px solid rgba(59, 130, 246, 0.4)",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "0.25rem",
                                  cursor: "pointer"
                                }}
                                title="Click to view linked platform agreements"
                              >
                                <FileText size={12} /> {partnerPlatformAgreements.length} Platform Ag. ↗
                              </span>
                            )}
                            {ndasCount > 0 && (
                              <span className="badge badge-success" style={{ fontSize: "0.72rem", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                                <CheckCircle2 size={12} /> {ndasCount} NDA(s)
                              </span>
                            )}
                            {teamingCount > 0 && (
                              <span className="badge" style={{ fontSize: "0.72rem", backgroundColor: "rgba(59, 130, 246, 0.15)", color: "#3B82F6", border: "1px solid rgba(59, 130, 246, 0.3)", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                                <FileText size={12} /> {teamingCount} Teaming Ag.
                              </span>
                            )}
                            {otherCount > 0 && (
                              <span className="badge badge-secondary" style={{ fontSize: "0.72rem", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                                <FileText size={12} /> {otherCount} Other
                              </span>
                            )}
                            {partnerPlatformAgreements.length === 0 && ndasCount === 0 && teamingCount === 0 && otherCount === 0 && (
                              <span style={{ fontSize: "0.85rem", fontStyle: "italic", color: "var(--text-muted)" }}>No agreements.</span>
                            )}
                          </div>
                        </td>

                        {/* Actions */}
                        <td style={{ padding: "1rem 1.5rem", textAlign: "right", verticalAlign: "middle" }}>
                          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                            <button
                              className="btn btn-secondary"
                              onClick={() => handleOpenEditPartner(partner)}
                              style={{ padding: "0.35rem" }}
                              title="Edit Partner"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              className="btn btn-secondary"
                              onClick={() => handleDeletePartner(partner)}
                              style={{ padding: "0.35rem", color: "var(--danger)", borderColor: "var(--danger)" }}
                              title="Delete Partner"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* TAB 2: AGREEMENTS */}
      {mainTab === "agreements" && (
        <div className="animate-fade-in">
          {/* Agreements Toolbar */}
          <div className="card glass-card" style={{ padding: "1rem", marginBottom: "1.5rem", display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
            <div className="input-with-icon" style={{ flex: 1, minWidth: "250px" }}>
              <Search className="input-icon" size={18} />
              <input
                type="text"
                className="form-input icon-padding"
                placeholder="Search by agreement title, partner, customer..."
                value={agreementSearch}
                onChange={(e) => setAgreementSearch(e.target.value)}
                style={{ margin: 0 }}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span className="text-secondary" style={{ fontSize: "0.875rem", fontWeight: 500 }}>Status:</span>
              <select
                className="form-input"
                value={agreementStatusFilter}
                onChange={(e) => setAgreementStatusFilter(e.target.value)}
                style={{ margin: 0, padding: "0.4rem 2rem 0.4rem 1rem", fontSize: "0.875rem", height: "38px", backgroundColor: "var(--bg-card)" }}
              >
                <option value="All">All Statuses</option>
                <option value="Draft">Draft</option>
                <option value="Under Review">Under Review</option>
                <option value="Executed">Executed</option>
                <option value="Archived">Archived</option>
              </select>
            </div>
          </div>

          {/* Agreements Table */}
          {loadingAgreements ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "4rem" }}>
              <Loader2 className="spinner-icon" size={32} />
              <span style={{ marginLeft: "0.75rem", color: "var(--text-secondary)" }}>Loading agreements...</span>
            </div>
          ) : errorAgreements ? (
            <div className="alert alert-danger">{errorAgreements}</div>
          ) : filteredAgreements.length === 0 ? (
            <div className="card glass-card" style={{ padding: "3rem", textAlign: "center", color: "var(--text-secondary)" }}>
              <p style={{ margin: 0, fontSize: "0.95rem", fontStyle: "italic" }}>
                No agreements found. Click "+ Create Agreement" to draft a new Teaming Agreement linked to a partner.
              </p>
            </div>
          ) : (
            <div className="card glass-card" style={{ padding: 0, overflowX: "auto" }}>
              <table className="table" style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--border-color)", backgroundColor: "rgba(255,255,255,0.01)" }}>
                    <th style={{ padding: "1rem 1.5rem", fontWeight: 600, fontSize: "0.85rem", color: "var(--text-muted)" }}>Agreement Title & Template</th>
                    <th style={{ padding: "1rem 1.5rem", fontWeight: 600, fontSize: "0.85rem", color: "var(--text-muted)" }}>Partner Organization</th>
                    <th style={{ padding: "1rem 1.5rem", fontWeight: 600, fontSize: "0.85rem", color: "var(--text-muted)" }}>Target Customer</th>
                    <th style={{ padding: "1rem 1.5rem", fontWeight: 600, fontSize: "0.85rem", color: "var(--text-muted)" }}>Effective Date</th>
                    <th style={{ padding: "1rem 1.5rem", fontWeight: 600, fontSize: "0.85rem", color: "var(--text-muted)" }}>Status</th>
                    <th style={{ padding: "1rem 1.5rem", fontWeight: 600, fontSize: "0.85rem", color: "var(--text-muted)", width: "160px", textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAgreements.map((agr) => {
                    const statusClass =
                      agr.status === "Executed" ? "badge-success" :
                      agr.status === "Under Review" ? "badge-primary" :
                      agr.status === "Archived" ? "badge-secondary" :
                      "badge-secondary";

                    return (
                      <tr
                        key={agr.id}
                        style={{ borderBottom: "1px solid var(--border-color)", transition: "background-color 0.2s" }}
                        className="hover-row"
                      >
                        {/* Title & Template */}
                        <td style={{ padding: "1rem 1.5rem", verticalAlign: "top" }}>
                          <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>{agr.title}</div>
                          <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginTop: "0.2rem" }}>
                            Template: {agr.templateTitle || "Teaming Agreement"}
                          </div>
                        </td>

                        {/* Partner Organization */}
                        <td style={{ padding: "1rem 1.5rem", verticalAlign: "top" }}>
                          <div style={{ fontWeight: 500, color: "var(--text-primary)" }}>
                            {agr.partnerCompany || agr.partnerName || "N/A"}
                          </div>
                          {agr.partnerState && (
                            <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                              State: {agr.partnerState}
                            </div>
                          )}
                        </td>

                        {/* Target Customer */}
                        <td style={{ padding: "1rem 1.5rem", verticalAlign: "top" }}>
                          <div style={{ fontSize: "0.85rem", color: "var(--text-primary)" }}>
                            {agr.customerName || "N/A"}
                          </div>
                          {agr.customerAcronym && (
                            <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                              ({agr.customerAcronym})
                            </div>
                          )}
                        </td>

                        {/* Effective Date */}
                        <td style={{ padding: "1rem 1.5rem", verticalAlign: "top", fontSize: "0.85rem", color: "var(--text-primary)" }}>
                          {agr.effectiveDate || "N/A"}
                        </td>

                        {/* Status */}
                        <td style={{ padding: "1rem 1.5rem", verticalAlign: "top" }}>
                          <span className={`badge ${statusClass}`} style={{ fontSize: "0.75rem" }}>
                            {agr.status || "Draft"}
                          </span>
                        </td>

                        {/* Actions */}
                        <td style={{ padding: "1rem 1.5rem", textAlign: "right", verticalAlign: "middle" }}>
                          <div style={{ display: "flex", gap: "0.4rem", justifyContent: "flex-end" }}>
                            <button
                              className="btn btn-secondary"
                              onClick={() => handlePrint(agr)}
                              style={{ padding: "0.3rem 0.5rem" }}
                              title="Print / PDF Preview"
                            >
                              <Printer size={15} />
                            </button>
                            <button
                              className="btn btn-secondary"
                              onClick={() => handleExportWord(agr)}
                              style={{ padding: "0.3rem 0.5rem" }}
                              title="Export to Word (.doc)"
                            >
                              <Download size={15} />
                            </button>
                            <button
                              className="btn btn-secondary"
                              onClick={() => handleOpenEditAgreement(agr)}
                              style={{ padding: "0.3rem 0.5rem" }}
                              title="Edit Agreement"
                            >
                              <Edit size={15} />
                            </button>
                            <button
                              className="btn btn-secondary"
                              onClick={() => handleDeleteAgreement(agr)}
                              style={{ padding: "0.3rem 0.5rem", color: "var(--danger)", borderColor: "var(--danger)" }}
                              title="Delete Agreement"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Partner Add/Edit Modal */}
      {partnerModalOpen && (
        <PartnerModal
          partner={selectedPartner}
          categories={categories}
          agreements={agreements}
          onOpenAgreement={(agr) => handleOpenEditAgreement(agr)}
          onClose={() => setPartnerModalOpen(false)}
          onSave={handleSavePartner}
          uploadPartnerAttachment={uploadPartnerAttachment}
          deletePartnerAttachment={deletePartnerAttachment}
        />
      )}

      {/* Agreement Add/Edit Modal */}
      {agreementModalOpen && (
        <PartnerAgreementModal
          agreement={selectedAgreement}
          partners={partners}
          templates={templates}
          companies={allCompanyLogos}
          companyLogo={activeLogoUrl}
          onClose={() => setAgreementModalOpen(false)}
          onSave={handleSaveAgreement}
        />
      )}
    </div>
  );
}
